import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { AppCard, AppNoInfo, AppRemixIcon } from "@/layout/AppComponents";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import errorHandler from "@/layout/components/error/ErrorHandler";
import { axiosPath } from "../../../routing/helpers/router";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import Translator from "../../../layout/components/helpers/Translator";
import TotalEnergiesRiskSidebar from "../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Sidebar/TotalEnergiesRiskSidebar";
import ProcessLayer from "../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Switch/Process/ProcessLayer";
import ProcessAdd from "../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Switch/Process/ProcessAdd";
import * as moduleAction from "../../../store/actions/moduleAction";

export default function ModuleTotalEnergiesRiskProcessListScreen() {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level,
  );
  const loading = useSelector((state) => state.currentPage?.loading);
  const processes = useSelector((state) => state.currentPage?.processes);
  const [collapsed, setCollapsed] = useState(true);
  const [showAddModal, setShowAddModal] = useState();

  useEffect(() => {
    dispatch(currentPageAction.change("loading", true));
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD PROCESSES
  const loadProcesses = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_totalEnergiesRisk_process_list"), {
          moduleBridgeHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change("processes", data, true));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() =>
          dispatch(currentPageAction.change("loading", false, true)),
        ),
    [moduleBridgeHex],
  );

  useEffect(() => {
    if (loading) loadProcesses();
  }, [loading]);

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath("api_r_user_module_totalEnergiesRisk_getModuleData"),
        "moduleData",
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "permissions",
          {
            moduleBridgeHex,
            subType: "permissions",
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "userBridge",
          {
            moduleBridgeHex,
            subType: "userBridge",
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);

  if (!moduleData) return <AppNoInfo />;

  // < LOAD PROCESSES
  if (permissionsLevel === 0) {
    return (
      <Container>
        <TotalEnergiesRiskSidebar activeRoute="user_module_totalEnergiesRisk_process_list" />
        <BreadcrumbHandler
          paths={
            moduleData
              ? [
                  {
                    name: moduleData?.moduleName,
                    route: {
                      name: "user_module_totalEnergiesRisk_risk_list",
                      params: {
                        moduleBridgeHex,
                        page: 1,
                        filtersHex: 0,
                      },
                    },
                  },
                  { name: "Procesos" },
                ]
              : [{ name: "..." }]
          }
        />
        <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
          <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-xs btn-outline-light d-inline-flex mr-3"
                onClick={() => setShowAddModal("process")}
              >
                Nuevo proceso
              </button>
              <button
                type="button"
                className="btn btn-xs btn-outline-light d-inline-flex"
                onClick={() => setShowAddModal("subprocess")}
              >
                Nuevo subproceso
              </button>
            </div>
            {loading ? (
              <AppNoInfo minHeight={window.innerHeight - 60} />
            ) : processes?.length > 0 ? (
              <AppCard header="Procesos" className="mb-2">
                <span
                  className="btn btn-outline-light btn-xs mb-3 mr-2 d-inline-flex align-items-center"
                  onClick={() => setCollapsed(true)}
                >
                  <AppRemixIcon icon="sort-asc" className="pr-1" noVariants />
                  <Translator file="general" name="general.collapse-all" />
                </span>
                <span
                  className="btn btn-outline-light btn-xs mb-3 d-inline-flex align-items-center"
                  onClick={() => setCollapsed(false)}
                >
                  <AppRemixIcon icon="sort-desc" className="pr-1" noVariants />
                  <Translator file="general" name="general.expand-all" />
                </span>
                {processes?.map((p) => (
                  <ProcessLayer key={p.id} process={p} collapsed={collapsed} />
                ))}
              </AppCard>
            ) : (
              <div className="alert alert-info">
                Aún no existe ningún proceso
              </div>
            )}
          </div>
        </main>
        <ProcessAdd showModal={showAddModal} setShowModal={setShowAddModal} />
      </Container>
    );
  } else {
    return <></>;
  }
}

const Container = styled.div`
  .tree-view {
    overflow-y: hidden;
  }

  .tree-view_children {
    margin-left: 16px;
    border-left: 1px dotted black;
  }

  .tree-view_children-collapsed {
    height: 0;
  }

  .card-body > .tree-view > .tree-view_item {
    .tree-view_arrow {
      cursor: pointer;
      margin-right: 6px;
      display: inline-block;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
    .tree-view_arrow:after {
      content: "▾";
    }
    .tree-view_arrow-collapsed {
      -webkit-transform: rotate(-90deg);
      -moz-transform: rotate(-90deg);
      -ms-transform: rotate(-90deg);
      transform: rotate(-90deg);
    }
  }
`;
